/**
 * Content-envelope builder — pure helpers for constructing +
 * decoding the JSON envelope stored in a pending record's `body`
 * attribute.
 *
 * The backend adapter (`services/src/api-layer/handlers/_entries/
 * _adapters/_adapter-helpers.ts::projectRecord`) decodes this
 * envelope on read and surfaces the structured fields as
 * `ApprovedContentItem` properties in the wire response. The SPA's
 * `<DailyPage>` + `<GamePage>` render off those structured fields.
 * The admin submit form therefore has to produce the envelope, not
 * plain text, if we want approved content to render correctly.
 *
 * Legacy fallback: the backend accepts a plain-text body too (any
 * string not starting with `{` is treated as `body` and every
 * structured field is undefined). This module lets the SPA emit
 * the rich JSON envelope so approvals project into proper cards.
 */

export type ContentType = 'fact' | 'news' | 'release';

/** Vendor + URL pair for release purchase-link list. */
export interface PurchaseLink {
  readonly vendor: string;
  readonly url: string;
}

/**
 * Fully-typed envelope. Every field is optional so plain-text
 * submissions still round-trip. The builder below writes only the
 * fields the operator supplied.
 */
export interface ContentEnvelope {
  readonly title?: string;
  readonly body?: string;
  readonly image?: string;
  readonly gallery?: ReadonlyArray<string>;
  readonly platforms?: ReadonlyArray<string>;
  readonly videos?: ReadonlyArray<string>;
  readonly purchaseLinks?: ReadonlyArray<PurchaseLink>;
  readonly gameId?: string;
  readonly source?: string;
  readonly sourceUrl?: string;
}

/**
 * Build a JSON string for the pending record `body` from a partial
 * envelope. Empty strings and empty arrays are dropped so the
 * backend adapter sees only fields the operator actually filled in.
 */
export function serializeEnvelope(envelope: ContentEnvelope): string {
  const out: Record<string, unknown> = {};
  if (envelope.title !== undefined && envelope.title.trim().length > 0) {
    out['title'] = envelope.title.trim();
  }
  if (envelope.body !== undefined && envelope.body.trim().length > 0) {
    out['body'] = envelope.body.trim();
  }
  if (envelope.image !== undefined && envelope.image.trim().length > 0) {
    out['image'] = envelope.image.trim();
  }
  if (envelope.gallery !== undefined && envelope.gallery.length > 0) {
    out['gallery'] = [...envelope.gallery];
  }
  if (envelope.platforms !== undefined && envelope.platforms.length > 0) {
    out['platforms'] = [...envelope.platforms];
  }
  if (envelope.videos !== undefined && envelope.videos.length > 0) {
    out['videos'] = [...envelope.videos];
  }
  if (envelope.purchaseLinks !== undefined && envelope.purchaseLinks.length > 0) {
    out['purchaseLinks'] = envelope.purchaseLinks.map((p) => ({
      vendor: p.vendor,
      url: p.url,
    }));
  }
  if (envelope.gameId !== undefined && envelope.gameId.trim().length > 0) {
    out['gameId'] = envelope.gameId.trim();
  }
  if (envelope.source !== undefined && envelope.source.trim().length > 0) {
    out['source'] = envelope.source.trim();
  }
  if (envelope.sourceUrl !== undefined && envelope.sourceUrl.trim().length > 0) {
    out['sourceUrl'] = envelope.sourceUrl.trim();
  }
  return JSON.stringify(out);
}

/**
 * Decode a possibly-JSON-encoded body string. Returns the parsed
 * envelope when the string starts with `{` and parses cleanly;
 * otherwise returns a `{ body: <raw> }` fallback so plain-text
 * pending items still render sensibly on the review surface.
 */
export function decodeEnvelope(rawBody: string): ContentEnvelope {
  const trimmed = rawBody.trim();
  if (!trimmed.startsWith('{')) return { body: trimmed };
  try {
    const parsed = JSON.parse(trimmed) as unknown;
    if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return { body: trimmed };
    }
    return coerce(parsed as Record<string, unknown>);
  } catch {
    return { body: trimmed };
  }
}

function coerce(o: Record<string, unknown>): ContentEnvelope {
  return {
    title: pickString(o['title']),
    body: pickString(o['body']),
    image: pickString(o['image']),
    gallery: pickStringArray(o['gallery']),
    platforms: pickStringArray(o['platforms']),
    videos: pickStringArray(o['videos']),
    purchaseLinks: pickPurchaseLinks(o['purchaseLinks']),
    gameId: pickString(o['gameId']),
    source: pickString(o['source']),
    sourceUrl: pickString(o['sourceUrl'] ?? o['source_url']),
  };
}

function pickString(v: unknown): string | undefined {
  return typeof v === 'string' && v.length > 0 ? v : undefined;
}

function pickStringArray(v: unknown): ReadonlyArray<string> | undefined {
  if (!Array.isArray(v) || v.length === 0) return undefined;
  if (!v.every((x) => typeof x === 'string')) return undefined;
  return v as ReadonlyArray<string>;
}

function pickPurchaseLinks(v: unknown): ReadonlyArray<PurchaseLink> | undefined {
  if (!Array.isArray(v) || v.length === 0) return undefined;
  const out: PurchaseLink[] = [];
  for (const entry of v) {
    if (
      entry !== null &&
      typeof entry === 'object' &&
      typeof (entry as Record<string, unknown>)['vendor'] === 'string' &&
      typeof (entry as Record<string, unknown>)['url'] === 'string'
    ) {
      out.push({
        vendor: (entry as { vendor: string }).vendor,
        url: (entry as { url: string }).url,
      });
    }
  }
  return out.length > 0 ? out : undefined;
}

/** Which envelope fields are relevant to each content type. */
export const ENVELOPE_FIELDS_BY_TYPE: Record<
  ContentType,
  ReadonlyArray<keyof ContentEnvelope>
> = {
  fact: ['body', 'image', 'source', 'sourceUrl'],
  news: ['title', 'body', 'image', 'source', 'sourceUrl', 'gameId'],
  release: [
    'title',
    'body',
    'image',
    'platforms',
    'gallery',
    'videos',
    'purchaseLinks',
    'gameId',
    'source',
    'sourceUrl',
  ],
};

export const PLATFORM_IDS: ReadonlyArray<{ readonly value: string; readonly label: string }> = [
  { value: 'pc', label: 'PC' },
  { value: 'xbox', label: 'Xbox' },
  { value: 'playstation', label: 'PlayStation' },
  { value: 'nintendo-switch', label: 'Nintendo Switch' },
  { value: 'steam-deck', label: 'Steam Deck' },
  { value: 'mobile', label: 'Mobile' },
];
